<template>
	<div class="page-events">
		<div class="row align-items-center mb-3">
			<div class="col">
				<h2 class="my-0 text-danger">

				</h2>
			</div>
			<div class="col-auto" v-if="j && j.website">
				<a :href="j.website" target="_blank" class="btn btn-outline-primary btn-sm me-2">
					View on website
				</a>
			</div>
		</div>

		<div class="page-emergency-alerts">
			<div class="row justify-content-center">
				<div class="col-lg-10">
					<!-- Ticker Bar Section -->
					<div class="card mb-4 border-primary shadow-lg" v-if="hasTickerBar">
						<div class="card-header">
							<div class="d-flex align-items-center justify-content-between">
								<h5 class="my-0 text-primary">
									<font-awesome-icon :icon="['fas', 'scroll']" class="fa-shake me-2" />
									ACTIVE TICKER BAR
								</h5>
								<button @click="removeTicker" class="btn btn-outline-primary btn-sm"
									:disabled="tickerState === 'deleting'">
									<template v-if="tickerState === 'deleting'">
										<span class="spinner-border spinner-border-sm me-1" role="status"></span>
										Removing...
									</template>
									<template v-else>
										<font-awesome-icon :icon="['fas', 'times']" class="me-1" />
										Remove Ticker
									</template>
								</button>
							</div>
						</div>
						<div class="card-body px-5">
							<div class="alert alert-primary mb-3">
								<div class="d-flex align-items-center mb-2">
									<font-awesome-icon :icon="['fas', 'satellite-dish']" class="me-2 fa-lg" />
									<strong>Currently Broadcasting Through:</strong>
								</div>
								<ul class="list-unstyled mb-0 ms-4">
									<li>
										<font-awesome-icon :icon="['fas', 'globe']" class="me-2" />
										Website Ticker
										<small class="text-muted ms-2">- Displays on {{ j.website }}</small>
									</li>
								</ul>
							</div>

							<div class="alert alert-primary d-flex align-items-center mb-0 overflow-hidden">
								<div class="d-flex align-items-center flex-shrink-0 me-3">
									<font-awesome-icon :icon="['fas', 'bullhorn']" class="me-2" />
									<strong>Ticker Message:</strong>
								</div>
								<div class="ticker-content">
									<div class="ticker-scroll">
										<strong>{{ tickerTitle }}</strong>
										<span v-if="tickerTitle && tickerContent"> - </span>
										{{ tickerContent }}
									</div>
								</div>
							</div>
						</div>
					</div>

					<!-- Create Ticker Bar Form -->
					<div class="card mb-4" :class="{ 'opacity-50': hasTickerBar }">
						<div class="card-header">
							<div class="d-flex align-items-center justify-content-between">
								<h5 class="my-0">
									{{ hasTickerBar ? 'Update Ticker Bar' : 'Create Ticker Bar' }}
								</h5>
							</div>
						</div>

						<div class="card-body">
							<template v-if="hasTickerBar">
								<div class="alert alert-secondary mb-3">
									<font-awesome-icon :icon="['fas', 'info-circle']" class="me-2" />
									<strong>Note:</strong> Only one ticker bar can be active at a time. To create a new
									ticker bar, please remove the current one first.
								</div>
							</template>

							<form @submit.prevent="generateTickerContent" class="px-5 mb-4">
								<div v-if="tickerState === 'generating'" class="">
									<span class="spinner-border spinner-border-sm text-primary" role="status"></span>
									Generating ticker for "{{ tickerPrompt }}"
								</div>
								<div v-else class="form-floating">
									<input type="text" class="form-control" id="ticker-prompt"
										placeholder="Ex: road construction update" v-model="tickerPrompt" required
										minlength="5" :disabled="tickerState === 'generating'" />
									<label for="ticker-prompt">
										✨ Generate with AI
									</label>
								</div>
							</form>

							<hr class="bg-primary-100" />

							<div class="form-group mb-4">
								<label class="form-label d-flex align-items-center">
									<strong>Ticker Title</strong>
									<span class="text-danger ms-1">*</span>
								</label>
								<input type="text" class="form-control bg-light" :value="tickerTitle" readonly
									required />
							</div>

							<div class="form-group">
								<label class="form-label d-flex align-items-center">
									<strong>Ticker Content</strong>
									<span class="text-danger ms-1">*</span>
								</label>
								<textarea v-model="tickerContent" class="form-control" rows="3"
									placeholder="Enter ticker bar content..." required
									:disabled="hasTickerBar"></textarea>
							</div>

							<div class="d-flex justify-content-end mt-4">
								<button class="btn btn-primary" @click="updateTicker"
									:disabled="tickerState === 'updating' || (hasTickerBar && !tickerChanged)">
									<template v-if="tickerState === 'updating'">
										<span class="spinner-border spinner-border-sm me-2" role="status"></span>
										{{ hasTickerBar ? 'Updating...' : 'Creating...' }}
									</template>
									<template v-else>
										<font-awesome-icon :icon="['fas', 'check']" class="me-2" />
										{{ hasTickerBar ? 'Update Ticker' : 'Create Ticker' }}
									</template>
								</button>
							</div>
						</div>
					</div>

					<!-- Current Alert Card -->
					<div class="card mb-4 border-danger shadow-lg alert-pulse" v-if="hasActiveAlert">
						<div class="card-header">
							<div class="d-flex align-items-center justify-content-between">
								<h5 class="my-0 text-danger">
									<font-awesome-icon :icon="['fas', 'bell']" class="me-2 fa-shake" />
									ACTIVE EMERGENCY ALERT
								</h5>
								<button @click="removeAlert" class="btn btn-outline-danger btn-sm"
									:disabled="alertState === 'deleting'">
									<template v-if="alertState === 'deleting'">
										<span class="spinner-border spinner-border-sm me-1" role="status"></span>
										Removing...
									</template>
									<template v-else>
										<font-awesome-icon :icon="['fas', 'times']" class="me-1" />
										Remove Alert
									</template>
								</button>
							</div>
						</div>
						<div class="card-body px-5">
							<div class="alert alert-danger mb-3">
								<div class="d-flex align-items-center mb-2">
									<font-awesome-icon :icon="['fas', 'satellite-dish']" class="me-2 fa-lg" />
									<strong>Currently Broadcasting Through:</strong>
								</div>
								<ul class="list-unstyled mb-0 ms-4">
									<li>
										<font-awesome-icon :icon="['fas', 'globe']" class="me-2" />
										Website Ticker
										<small class="text-muted ms-2">- Displays on {{ j.website }}</small>
									</li>
								</ul>
							</div>

							<div class="alert alert-danger d-flex align-items-center mb-0 overflow-hidden">
								<div class="d-flex align-items-center flex-shrink-0 me-3">
									<font-awesome-icon :icon="['fas', 'bell']" class="fa-shake me-2" />
									<strong>Emergency Message:</strong>
								</div>
								<div class="ticker-content">
									<div class="ticker-scroll">
										<strong>{{ emergencyAlert.title }}</strong>
										<span v-if="emergencyAlert.title && emergencyAlert.content"> - </span>
										{{ emergencyAlert.content }}
									</div>
								</div>
							</div>
						</div>
					</div>

					<!-- New Alert Form -->
					<div class="card mb-5" :class="{ 'opacity-50': hasActiveAlert }">
						<div class="card-header">
							<div class="d-flex align-items-center justify-content-between">
								<h5 class="my-0">
									{{ hasActiveAlert ? 'Create New Emergency Alert' : 'Create Emergency Alert' }}
								</h5>
								<help-button url="https://townweb.notion.site/Emergency-Alerts-Guide"
									text="How to send Emergency Alerts"></help-button>
							</div>
						</div>
						<div class="card-body px-5">
							<form @submit.prevent="createEmergencyAlert">
								<div class="row">
									<div class="col-12">
										<form @submit.prevent="generateEmergencyAlert" class="px-5">
											<div v-if="alertState === 'generating'" class="">
												<span class="spinner-border spinner-border-sm text-primary"
													role="status"></span>
												Generating alert for "{{ alertPrompt }}"
											</div>
											<div v-else class="form-floating mb-3">
												<input type="text" class="form-control" id="emergency-alert-prompt"
													placeholder="Ex: water main break on Main St" v-model="alertPrompt"
													required minlength="5"
													:disabled="hasActiveAlert || alertState === 'generating'" />
												<label for="emergency-alert-prompt">
													✨ Generate with AI
												</label>
											</div>
										</form>

										<hr class="bg-primary-100" />

										<div class="form-group mb-4">
											<label class="form-label d-flex align-items-center">
												<strong>Alert Title</strong>
												<span class="text-danger ms-1">*</span>
											</label>
											<input type="text" class="form-control" placeholder="Short and clear title"
												v-model="emergencyAlert.title" required :disabled="hasActiveAlert" />
										</div>

										<div class="form-group mb-4">
											<label class="form-label d-flex align-items-center">
												<strong>Emergency Message</strong>
												<span class="text-danger ms-1">*</span>
											</label>
											<textarea v-model="emergencyAlert.content" class="form-control" rows="5"
												placeholder="Write your emergency alert content here..." required
												:disabled="hasActiveAlert"></textarea>
										</div>

										<div class="form-group mb-4">
											<label class="form-label d-flex align-items-center" for="notice-channels">
												<font-awesome-icon :icon="['fas', 'satellite-dish']" class="me-2" />
												<strong>Where to Post</strong>
											</label>
											<div class="row row-cols-1 row-cols-md-2 g-3">
												<div class="col">
													<div class="p-3 rounded-1" :class="[
														emergencyAlert.channels.includes('website')
															? 'bg-danger-50'
															: 'bg-neutral-50',
													]">
														<div class="form-check form-switch mb-2">
															<input class="form-check-input" type="checkbox"
																role="switch" id="channel-website"
																v-model="emergencyAlert.channels" value="website"
																:disabled="hasActiveAlert" />
															<label class="form-check-label" for="channel-website">
																<font-awesome-icon :icon="['fas', 'globe']"
																	class="me-2" />
																Website Ticker
															</label>
														</div>
														<p class="mb-0">
															Display alert banner on <code>{{ j.website }}</code>
														</p>
													</div>
												</div>

												<div class="col">
													<div class="p-3 rounded-1" :class="[
														emergencyAlert.channels.includes('subscribers')
															? 'bg-success-50'
															: 'bg-neutral-50',
													]">
														<div class="form-check form-switch mb-2">
															<input class="form-check-input" type="checkbox"
																role="switch" id="channel-subscribers"
																v-model="emergencyAlert.channels" value="subscribers"
																:disabled="hasActiveAlert" />
															<label class="form-check-label"
																for="channel-subscribers">Send to
																Subscribers</label>
														</div>
														<p class="mb-0">
															Send by email or text to Subscribers
														</p>
													</div>
												</div>
											</div>
										</div>
									</div>
								</div>
							</form>
						</div>
					</div>

					<!-- Website Ticker Preview Card -->
					<div v-if="emergencyAlert.channels.includes('website')" class="card mb-5">
						<div class="card-header">
							<h5 class="my-0">
								Website Ticker
							</h5>
						</div>
						<div class="card-body">
							<div class="alert alert-danger d-flex align-items-center mb-0 overflow-hidden">
								<div class="d-flex align-items-center flex-shrink-0 me-3">
									<font-awesome-icon :icon="['fas', 'bell']" class="fa-shake me-2" />
									<strong>EMERGENCY ALERT:</strong>
								</div>
								<div class="ticker-content">
									<div class="ticker-scroll">
										<strong>{{ emergencyAlert.title }}</strong>
										<span v-if="emergencyAlert.title && emergencyAlert.content"> - </span>
										{{ emergencyAlert.content }}
									</div>
								</div>
							</div>
							<small class="text-muted d-block mt-2">
								<font-awesome-icon :icon="['fas', 'info-circle']" class="me-1" />
								This is how the alert will appear at the top of your website
							</small>

							<!-- Ticker Position Dropdown -->
							<div class="mt-4">
								<h4 class="h6">Website ticker position</h4>
								<select class="form-select" v-model="emergencyAlert.position"
									:disabled="hasActiveAlert">
									<option value="top">Top Ticker Bar</option>
									<option value="bottom">Bottom Ticker Bar</option>
									<option value="rightpopup">Right Corner Screen</option>
									<option value="leftpopup">Left Corner Screen</option>
								</select>
							</div>
						</div>
					</div>

					<!-- Subscribers Preview Card -->
					<div v-if="emergencyAlert.channels.includes('subscribers')" class="card mb-4">
					<div class="card-header">
						<h5 class="my-0">Send to Subscribers</h5>
					</div>
					<div class="card-body">
						<div class="card-body">
							<!-- SMS Preview -->
							<div class="preview-section border rounded-3 p-4 mb-4">
								<div class="d-flex align-items-center gap-2 mb-4">
									<div class="preview-icon-wrapper">
										<font-awesome-icon :icon="['fas', 'comment-sms']" class="text-primary" />
									</div>
									<h6 class="mb-0">SMS Preview</h6>
								</div>
								<div class="sms-preview-container">
									<div class="sms-preview-header">
										<div class="sms-time">9:41</div>
										<div class="sms-icons">
											<font-awesome-icon :icon="['fas', 'signal']" />
											<font-awesome-icon :icon="['fas', 'wifi']" />
											<font-awesome-icon :icon="['fas', 'battery-full']" />
										</div>
									</div>
									<div class="sms-messages">
										<div class="sms-bubble">
											<div class="sms-sender">{{ j.name }}</div>
											<div
												class="sms-content"
												contenteditable="true"
												@input="e => handleContentInput(e, 'sms', 'text')"
												ref="smsText"
												:class="{'empty-content': !emergencyAlert.sms.text}"
											></div>
											<div class="sms-time-sent">Now</div>
										</div>
									</div>
								</div>
							</div>

							<!-- Email Preview -->
							<div class="preview-section border rounded-3 p-4">
								<div class="d-flex align-items-center gap-2 mb-4">
									<div class="preview-icon-wrapper">
										<font-awesome-icon :icon="['fas', 'envelope']" class="text-primary" />
									</div>
									<h6 class="mb-0">Email Preview</h6>
								</div>
								<div class="email-preview-container">
									<div class="email-preview-header">
										<div class="d-flex align-items-center gap-2 mb-3">
											<img
												v-if="j.logo_url"
												:src="j.logo_url"
												class="rounded"
												width="40"
												height="40"
												alt="Logo"
											/>
											<div v-else class="rounded bg-neutral-200 d-flex align-items-center justify-content-center" style="width: 40px; height: 40px;">
												<font-awesome-icon :icon="['fas', 'building']" class="text-neutral-400" />
											</div>
											<div>
												<div class="fw-bold">{{ j.name }}</div>
												<div class="text-muted small">{{ j.email || 'notifications@emailheygov.com' }}</div>
											</div>
										</div>
										<div class="email-subject mb-3">
											<input
												type="text"
												class="form-control"
												v-model="emergencyAlert.email.subject"
												placeholder="Email subject line..."
												:disabled="hasActiveAlert"
											/>
										</div>
									</div>

									<div class="email-content p-4 border rounded-1">
										<div
											contenteditable="true"
											@input="e => handleContentInput(e, 'email', 'text')"
											ref="emailText"
											:class="{'empty-content': !emergencyAlert.email.text}"
										></div>

										<div class="email-cta mt-4 text-center">
											<a
												href="#"
												class="btn btn-primary"
												@click.prevent
											>
												View on Website →
											</a>
										</div>
									</div>

									<div class="email-footer mt-3 text-center text-muted">
										<small>You're receiving this message because you're a member of {{ j.name }}</small><br>
										<small>Powered by HeyGov</small>
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>

					<!-- Send Alert Card -->
					<div class="card mb-5">
						<div class="card-header">
							<h5 class="my-0">Send Emergency Alert</h5>
						</div>
						<div class="card-body text-center">
							<template v-if="emergencyAlert.channels.length">
								<p class="card-text">
									This emergency alert will be sent to {{ emergencyAlert.channels.join(', ') }}.
								</p>
								<p class="card-text">
									<button type="submit" class="btn btn-danger px-6"
										:disabled="hasActiveAlert || !emergencyAlert.content || alertState === 'submitting'"
										@click="createEmergencyAlert">
										<template v-if="alertState === 'submitting'">
											<span class="spinner-border spinner-border-sm me-2" role="status"></span>
											Broadcasting...
										</template>
										<template v-else>
											<font-awesome-icon :icon="['fas', 'satellite-dish']" class="me-2" />
											Broadcast Emergency Alert
											<font-awesome-icon :icon="['fas', 'triangle-exclamation']" class="ms-2" />
										</template>
									</button>
								</p>
							</template>
							<div v-else class="alert alert-warning">
								Please select at least one channel to send this alert.
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
import { mapState } from 'vuex'
import Vue from 'vue'
import heyGovApi, { handleResponseError } from '@/api.js'
import { truncateString } from '../../lib/strings'
import { format } from 'date-fns'

export default {
	name: 'EmergencyAlerts',
	components: {
		'help-button': () => import('@/components/HelpButton.vue')
	},
	data() {
		return {
			tickerPrompt: '',
			tickerTitle: this.generateTickerTitle(),
			tickerContent: '',
			tickerState: 'idle',
			originalTickerTitle: '',
			originalTickerContent: '',
			hasTickerBar: false,
			tickerId: null,
			hasActiveAlert: false,
			alertPrompt: '',
			alertState: 'idle',
			alertId: null,
			emergencyAlert: {
				title: '',
				content: '',
				channels: [],
				position: 'top',
				sms: {
					text: ''
				},
				email: {
					subject: '',
					text: ''
				}
			},
		}
	},
	computed: {
		...mapState(['apiUrl', 'j']),
		tickerChanged() {
			return this.tickerTitle !== this.originalTickerTitle ||
				this.tickerContent !== this.originalTickerContent
		}
	},
	created() {
		this.checkActiveAlert()
		this.checkActiveTicker()
	},
	methods: {
		checkActiveAlert() {
			heyGovApi
				.get(`${this.j.slug}/alerts`)
				.then(({ data }) => {
					this.hasActiveAlert = !!data?.data?.data?.length
					if (data?.data?.data?.length) {
						this.alertId = data.data.data[0].id
						this.emergencyAlert.title = data.data.data[0].title
						this.emergencyAlert.content = data.data.data[0].content
						this.emergencyAlert.channels = []
					} else {
						this.emergencyAlert.channels = ['website']
					}
				})
				.catch(handleResponseError('Failed to check active alert ({error})'))
		},

		generateEmergencyAlert() {
			this.alertState = 'generating'
			console.log('Generating emergency alert with prompt:', this.alertPrompt)

			heyGovApi
				.post(`${this.j.slug}/generate-draft`, {
					prompt: `Generate emergency alert for ${this.j.name} about this prompt: ${this.alertPrompt}. IMPORTANT: The content MUST be exactly one short sentence, no more and no less.`,
				})
				.then(({ data }) => {
					this.emergencyAlert.title = data.draft?.website?.title || ''
					this.emergencyAlert.content = data.draft?.website?.body || ''

					// Populate SMS content
					this.emergencyAlert.sms.text = data.draft?.sms?.text || ''

					// Populate email content
					this.emergencyAlert.email.subject = data.draft?.email?.subject || ''
					this.emergencyAlert.email.text = data.draft?.email?.body || ''

					Vue.toasted.success(`Generated draft for "${truncateString(this.alertPrompt, 22)}"`)
				})
				.catch(handleResponseError('Failed to generate alert content ({error})'))
				.finally(() => {
					this.alertState = 'idle'
				})
		},

		createEmergencyAlert() {
			this.alertState = 'submitting'

			const payload = {
				status: 'publish',
			}
			if (this.emergencyAlert.channels.includes('website')) {
				payload.title = this.emergencyAlert.title
				payload.content = this.emergencyAlert.content
				payload.ticker_position = this.emergencyAlert.position
				payload.website_ticker = true
			}

			if (this.emergencyAlert.channels.includes('subscribers')) {
				payload.sms = {
					content: this.emergencyAlert.sms.text
				};
				payload.email = {
					subject: this.emergencyAlert.email.subject,
					content: this.emergencyAlert.email.text
				};
			}

			heyGovApi
				.post(`${this.j.slug}/alerts`, payload)
				.then(() => {
					Vue.toasted.success('Emergency alert sent successfully')
					this.hasActiveAlert = true
					this.alertPrompt = ''
					this.emergencyAlert.channels = []
					this.checkActiveAlert()
				})
				.catch(handleResponseError('Failed to send emergency alert ({error})'))
				.finally(() => {
					this.alertState = 'idle'
				})
		},

		removeAlert() {
			if (!this.alertId) {
				Vue.toasted.error('No active alert to remove')
				return
			}

			this.alertState = 'deleting'

			heyGovApi
				.delete(`${this.j.slug}/alerts/${this.alertId}`)
				.then(() => {
					Vue.toasted.success('Emergency alert removed')
					this.hasActiveAlert = false
					this.alertId = null
					this.emergencyAlert.title = ''
					this.emergencyAlert.content = ''
					this.emergencyAlert.channels = ['website']
				})
				.catch(handleResponseError('Failed to remove emergency alert ({error})'))
				.finally(() => {
					this.alertState = 'idle'
				})
		},

		checkActiveTicker() {
			heyGovApi
				.get(`${this.j.slug}/alerts/ticker-bars`)
				.then(({ data }) => {
					this.hasTickerBar = !!data?.data?.data?.length
					if (data?.data?.data?.length) {
						const ticker = data.data.data[0]
						this.tickerId = ticker.id
						this.tickerTitle = ticker.title || ''
						this.tickerContent = ticker.content || ''
						this.originalTickerTitle = this.tickerTitle
						this.originalTickerContent = this.tickerContent
					} else {
						this.tickerTitle = this.generateTickerTitle()
					}
				})
				.catch(handleResponseError('Failed to check active ticker ({error})'))
		},

		generateTickerTitle() {
			const date = new Date()
			return `Notice ${format(date, 'MM-dd-yyyy')}`
		},

		generateTickerContent() {
			this.tickerState = 'generating'
			console.log('Generating ticker with prompt:', this.tickerPrompt)

			heyGovApi
				.post(`${this.j.slug}/generate-draft`, {
					prompt: `Generate a website ticker update for ${this.j.name} about this prompt: ${this.tickerPrompt}. The content should be exactly one short sentence.`,
				})
				.then(({ data }) => {
					this.tickerTitle = this.generateTickerTitle()
					this.tickerContent = data.draft?.website?.body || ''
					Vue.toasted.success(`Generated ticker for "${truncateString(this.tickerPrompt, 22)}"`)
				})
				.catch(handleResponseError('Failed to generate ticker content ({error})'))
				.finally(() => {
					this.tickerState = 'idle'
				})
		},

		updateTicker() {
			if (!this.tickerContent) {
				Vue.toasted.error('Please enter ticker content')
				return
			}

			this.tickerState = 'updating'

			const payload = {
				title: this.tickerTitle,
				content: this.tickerContent
			}

			heyGovApi
				.post(`${this.j.slug}/alerts/ticker-bars`, payload)
				.then(() => {
					Vue.toasted.success(`Ticker ${this.hasTickerBar ? 'updated' : 'created'} successfully`)
					this.tickerPrompt = ''
					this.checkActiveTicker()
				})
				.catch(handleResponseError(`Failed to ${this.hasTickerBar ? 'update' : 'create'} ticker ({error})`))
				.finally(() => {
					this.tickerState = 'idle'
				})
		},

		removeTicker() {
			if (!this.tickerId && !this.hasTickerBar) {
				Vue.toasted.error('No active ticker to remove')
				return
			}

			this.tickerState = 'deleting'

			heyGovApi
				.delete(`${this.j.slug}/alerts/ticker-bars/${this.tickerId}`)
				.then(() => {
					Vue.toasted.success('Ticker removed successfully')
					this.hasTickerBar = false
					this.tickerId = null
					this.tickerTitle = this.generateTickerTitle()
					this.tickerContent = ''
					this.originalTickerTitle = ''
					this.originalTickerContent = ''
				})
				.catch(handleResponseError('Failed to remove ticker ({error})'))
				.finally(() => {
					this.tickerState = 'idle'
				})
		},

		handleContentInput(e, channel, field) {
			const text = e.target.innerText
			const currentValue = this.emergencyAlert[channel][field]

			if (currentValue !== text) {
				this.emergencyAlert[channel][field] = text
			}
		},

		updateContent(channel, field, text) {
			const ref = `${channel}Text`
			if (this.$refs[ref]) {
				const currentText = this.$refs[ref].innerText
				if (currentText !== text) {
					this.$refs[ref].innerText = text || ''
				}
			}
		}
	},
	watch: {
		emergencyAlert: {
			deep: true,
			handler(newAlert) {
				this.updateContent('sms', 'text', newAlert.sms.text)
				this.updateContent('email', 'text', newAlert.email.text)
			}
		}
	}
}
</script>

<style lang="scss" scoped>
@keyframes alert-pulse {
	0% {
		box-shadow: 0 0 0 0 rgba(220, 53, 69, 0.4);
	}

	70% {
		box-shadow: 0 0 0 10px rgba(220, 53, 69, 0);
	}

	100% {
		box-shadow: 0 0 0 0 rgba(220, 53, 69, 0);
	}
}

.alert-pulse {
	animation: alert-pulse 2s infinite;
}

@keyframes ticker {
	0% {
		transform: translateX(0);
	}

	100% {
		transform: translateX(-100%);
	}
}

.ticker-scroll:hover {
	animation-play-state: paused;
}

.ticker-content {
	position: relative;
	overflow: hidden;
	white-space: nowrap;
	width: 100%;
	margin: 0;
	padding: 0;
}

.ticker-scroll {
	display: inline-block;
	padding-left: 100%;
	animation: ticker 20s linear infinite;
	white-space: nowrap;
}

[contenteditable] {
	outline: none;
	padding: 8px;
	border-radius: 4px;

	&:focus {
		background: #f8f9fa;
	}
}

.empty-content {
	&:empty {
		&:before {
			content: 'Write your content here...';
			color: #6c757d;
		}
	}
}

.sms {
	&-preview {
		&-container {
			max-width: 375px;
			margin: 0 auto;
			background: #f8f9fa;
			border-radius: 2rem;
			padding: 1rem;
			box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
		}

		&-header {
			display: flex;
			justify-content: space-between;
			align-items: center;
			padding: 0.5rem 1rem;
			font-size: 0.875rem;
		}
	}

	&-icons {
		display: flex;
		gap: 0.5rem;
	}

	&-messages {
		padding: 1rem;
		background: white;
		border-radius: 1rem;
		min-height: 200px;
	}

	&-bubble {
		background: #e9ecef;
		padding: 0.75rem;
		border-radius: 1rem;
		max-width: 80%;
	}

	&-sender {
		font-weight: 600;
		font-size: 0.875rem;
		margin-bottom: 0.25rem;
	}

	&-content {
		min-height: 3rem;

		&.empty-content:empty:before {
			content: 'Write your SMS message here...';
		}
	}

	&-time-sent {
		font-size: 0.75rem;
		color: #6c757d;
		text-align: right;
		margin-top: 0.25rem;
	}
}

.email {
	&-preview-container {
		max-width: 600px;
		margin: 0 auto;
		background: white;
		padding: 1.5rem;
	}

	&-content {
		min-height: 200px;
		background: white;

		&.empty-content:empty:before {
			content: 'Write your email message here...';
		}
	}

	&-subject {
		input {
			border: none;
			border-bottom: 1px solid #dee2e6;
			border-radius: 0;
			padding: 0.5rem 0;
			font-weight: 600;

			&:focus {
				box-shadow: none;
				border-color: #0d6efd;
			}
		}
	}

	&-cta {
		border-top: 1px solid #dee2e6;
		padding-top: 1.5rem;
		margin-top: 1.5rem;

		.btn {
			padding: 0.5rem 1.5rem;
			font-weight: 500;
			border-radius: 4px;

			&:hover {
				transform: translateY(-1px);
				box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
			}
		}
	}
}

.preview {
	&-section {
		height: 100%;
		background: #fff;
		transition: all 0.2s ease;

		&:hover {
			border-color: #0d6efd !important;
			box-shadow: 0 0 0 1px #0d6efd;
		}
	}

	&-icon-wrapper {
		width: 32px;
		height: 32px;
		display: flex;
		align-items: center;
		justify-content: center;
		background: rgba(13, 110, 253, 0.1);
		border-radius: 8px;
	}
}
</style>
